/**
 * Metric definitions — the single source for every figure on every dashboard
 * and in every report.
 *
 * Spec §6 gives a worked example that pins these formulas exactly:
 *
 *   09:32 sent · 09:33 received · 09:41 read · 10:02 replied
 *   10:10 resolved · 10:15 closed
 *
 *   delivery 1m · read 8m · response 21m · total processing 43m
 *
 * All four reconcile against the definitions below; `metrics.test.ts` asserts
 * it, so a change to a formula fails the build rather than quietly moving every
 * number in the system.
 *
 * Traces: STAT-004, STAT-005, STAT-006, DASH-001, ADR-008, ADR-009.
 */

import type { Priority } from './enums';

// ---------------------------------------------------------------------------
// Elapsed-time metrics
// ---------------------------------------------------------------------------

export const ELAPSED_METRICS = [
  'deliveryTime',
  'readTime',
  'handlingTime',
  'responseTime',
  'resolutionTime',
  'totalProcessingTime',
] as const;

export type ElapsedMetric = (typeof ELAPSED_METRICS)[number];

export interface MetricDefinition {
  readonly key: ElapsedMetric;
  readonly label: string;
  readonly from: LifecycleTimestamp;
  readonly to: LifecycleTimestamp;
  readonly description: string;
}

export const LIFECYCLE_TIMESTAMPS = [
  'sentAt',
  'receivedAt',
  'readAt',
  'firstRepliedAt',
  'resolvedAt',
  'closedAt',
] as const;

export type LifecycleTimestamp = (typeof LIFECYCLE_TIMESTAMPS)[number];

export const METRIC_DEFINITIONS: Record<ElapsedMetric, MetricDefinition> = {
  deliveryTime: {
    key: 'deliveryTime',
    label: 'Delivery Time',
    from: 'sentAt',
    to: 'receivedAt',
    description: 'System latency between send and acknowledgement into the recipient inbox.',
  },
  readTime: {
    key: 'readTime',
    label: 'Read Time',
    from: 'receivedAt',
    to: 'readAt',
    description: 'How long a delivered confirmation waited before a recipient opened it.',
  },
  handlingTime: {
    key: 'handlingTime',
    label: 'Handling Time',
    from: 'readAt',
    to: 'firstRepliedAt',
    description:
      'Spec §6 "response time" — effort between opening and replying. Measures the ' +
      'recipient handling the work, not the requester waiting.',
  },
  responseTime: {
    key: 'responseTime',
    label: 'Response Time',
    from: 'sentAt',
    to: 'firstRepliedAt',
    description:
      'End-to-end wait experienced by the sender, and the measure the SLA is judged ' +
      'against. Measured from readAt instead, an unopened confirmation could never ' +
      'breach — see ADR-008.',
  },
  resolutionTime: {
    key: 'resolutionTime',
    label: 'Resolution Time',
    from: 'firstRepliedAt',
    to: 'resolvedAt',
    description: 'Between the first reply and the originator marking the matter resolved.',
  },
  totalProcessingTime: {
    key: 'totalProcessingTime',
    label: 'Total Processing Time',
    from: 'sentAt',
    to: 'closedAt',
    description: 'Whole lifecycle, from send to close.',
  },
};

/** The metric the SLA clock is judged against. ADR-008. */
export const SLA_METRIC: ElapsedMetric = 'responseTime';

export type LifecycleTimestamps = Partial<Record<LifecycleTimestamp, Date | null>>;

/**
 * Elapsed seconds for one metric, or `null` when either endpoint is unset.
 *
 * Returning `null` rather than 0 matters: an unreplied confirmation has *no*
 * response time, and averaging it as zero would silently flatter every
 * response-time figure in the system.
 */
export function elapsedSeconds(
  metric: ElapsedMetric,
  timestamps: LifecycleTimestamps,
): number | null {
  const { from, to } = METRIC_DEFINITIONS[metric];
  const start = timestamps[from];
  const end = timestamps[to];
  if (start == null || end == null) return null;
  return Math.round((end.getTime() - start.getTime()) / 1000);
}

export function allElapsed(timestamps: LifecycleTimestamps): Record<ElapsedMetric, number | null> {
  return Object.fromEntries(
    ELAPSED_METRICS.map((m) => [m, elapsedSeconds(m, timestamps)]),
  ) as Record<ElapsedMetric, number | null>;
}

// ---------------------------------------------------------------------------
// Rate metrics
// ---------------------------------------------------------------------------

export const RATE_METRICS = ['responseRate', 'readRate', 'closureRate'] as const;
export type RateMetric = (typeof RATE_METRICS)[number];

export interface RateDefinition {
  readonly key: RateMetric;
  readonly label: string;
  readonly numerator: string;
  readonly denominator: string;
}

export const RATE_DEFINITIONS: Record<RateMetric, RateDefinition> = {
  responseRate: {
    key: 'responseRate',
    label: 'Response Rate',
    numerator: 'confirmations with a recipient reply',
    denominator: 'confirmations sent (excluding drafts and cancellations)',
  },
  readRate: {
    key: 'readRate',
    label: 'Read Rate',
    numerator: 'confirmations opened by a recipient',
    denominator: 'confirmations delivered',
  },
  closureRate: {
    key: 'closureRate',
    label: 'Closure Rate',
    numerator: 'confirmations closed',
    denominator: 'confirmations sent (excluding drafts and cancellations)',
  },
};

/**
 * Percentage to one decimal place, or `null` for an empty denominator.
 *
 * `null` is deliberate — an office that has sent nothing has no response rate,
 * and rendering 0% would read as a failing office rather than an idle one.
 */
export function rate(numerator: number, denominator: number): number | null {
  if (denominator <= 0) return null;
  return Math.round((numerator / denominator) * 1000) / 10;
}

/**
 * Average from stored sums and counts.
 *
 * ADR-013: rollups store sums and denominators, never pre-computed averages,
 * so any period aggregates as SUM(sum)/SUM(n) with no averaging-of-averages
 * error.
 */
export function averageSeconds(secondsSum: number, count: number): number | null {
  if (count <= 0) return null;
  return Math.round(secondsSum / count);
}

// ---------------------------------------------------------------------------
// SLA
// ---------------------------------------------------------------------------

/**
 * Default response windows from Spec §14. These are *seed values only* — the
 * live figures come from the `sla_rules` table and are configurable per office
 * (SLA-001). Nothing in the application may read these constants at runtime.
 */
export const DEFAULT_SLA_HOURS: Record<Priority, number> = {
  NORMAL: 24,
  PRIORITY: 8,
  URGENT: 2,
};

/** Ageing buckets for the §14 distribution report. */
export const SLA_AGE_BUCKETS = [
  { key: 'under8h', label: 'Under 8 hours', minHours: 0, maxHours: 8 },
  { key: '8to24h', label: '8–24 hours', minHours: 8, maxHours: 24 },
  { key: 'over24h', label: 'Over 24 hours', minHours: 24, maxHours: null },
] as const;

export type SlaAgeBucket = (typeof SLA_AGE_BUCKETS)[number]['key'];

export function bucketForHours(hours: number): SlaAgeBucket {
  if (hours < 8) return 'under8h';
  if (hours < 24) return '8to24h';
  return 'over24h';
}

/** Fraction of the SLA window elapsed at which a confirmation is AT_RISK. */
export const SLA_AT_RISK_THRESHOLD = 0.75;

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

/**
 * Renders seconds in the Spec's own style: "1 min", "8 min", "2h 18m", "43m".
 * Defined here rather than in the UI so dashboards, reports and exports read
 * identically.
 */
export function formatDuration(seconds: number | null): string {
  if (seconds === null) return '—';
  if (seconds < 60) return `${seconds}s`;

  const totalMinutes = Math.round(seconds / 60);
  if (totalMinutes < 60) return `${totalMinutes}m`;

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours < 24) {
    return minutes === 0 ? `${hours}h` : `${hours}h ${String(minutes).padStart(2, '0')}m`;
  }

  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  return remainingHours === 0 ? `${days}d` : `${days}d ${remainingHours}h`;
}

export function formatRate(value: number | null): string {
  return value === null ? '—' : `${value.toFixed(1)}%`;
}
